import React from "react";

import { faCube } from "@fortawesome/pro-regular-svg-icons";
import { find } from "lodash-es";

import { GenericObject } from "src/api/flowTypes";
import { DisplayDate, DisplayDateTime } from "src/base-components/Date";
import {
  PropertyDefinitionOutput,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { parseJsonOrReturnAsIs } from "src/datasets/DatasetTable/utils";
import { JsonContent } from "src/entities/entityView/EntityDetailsSidePane";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { EntitySchemaResourcePage } from "src/entities/queries";
import { EnumPill } from "src/eventsCatalogue/tableConfig";
import { Environment } from "src/hooks/useEnvironment";
import { formatNumber } from "src/utils/numbers";
import { speakPythonPrimitive } from "src/utils/speakPython";

export const renderEventPropertyValue = (
  value: unknown,
  propertyDef: PropertyDefinitionOutput,
  environment: Environment,
  entitySchemas?: EntitySchemaResourcePage,
  key?: string,
): React.ReactNode => {
  if (value === undefined || value === null) return null;

  switch (propertyDef.type) {
    case PropertyDefinitionOutputTypeEnum.DATETIME:
      return <DisplayDateTime value={value as string} />;

    case PropertyDefinitionOutputTypeEnum.DATE:
      return <DisplayDate value={value as string} />;

    case PropertyDefinitionOutputTypeEnum.INTEGER:
    case PropertyDefinitionOutputTypeEnum.NUMBER:
      return (
        <span className="font-code-12">{formatNumber(value as number)}</span>
      );

    case PropertyDefinitionOutputTypeEnum.ENUM: {
      const parsedValue = parseJsonOrReturnAsIs(value as string);
      const enumValue = find(propertyDef.enum_schema?.values, [
        "value",
        parsedValue,
      ]);
      return enumValue ? <EnumPill enumValue={enumValue} /> : String(value);
    }

    case PropertyDefinitionOutputTypeEnum.RELATION: {
      const relationData = value as GenericObject;
      const relationSchema = findSchema(
        relationData._type as string,
        entitySchemas,
      );
      return (
        <EntityDetailsWindowPill
          entityIcon={relationSchema ? getSchemaIcon(relationSchema) : faCube}
          environment={environment}
          fullWidth={false}
          id={relationData._id as string}
          name={relationData._id as string}
          schemaId={relationData._type as string}
        />
      );
    }

    case PropertyDefinitionOutputTypeEnum.BOOLEAN:
      return speakPythonPrimitive(String(value));

    case PropertyDefinitionOutputTypeEnum.ADDRESS:
      return (
        <div className="max-w-50">
          <JsonContent
            content={value as GenericObject}
            title={propertyDef.display_name ?? key ?? "Address"}
          />
        </div>
      );

    default:
      return String(value);
  }
};
